import { Box } from "@mui/material";
import { SxProps, Theme } from "@mui/material/styles";
import {
  DataGridPremiumProps,
  GridColDef,
  GridRowId,
  GridValidRowModel,
} from "@mui/x-data-grid-premium";
import { HotkeyCompliantDataGrid } from "components/common/dataGrid";
import { NumberCell } from "components/common/dataGrid/NumberCell";
import { useTenantTranslation } from "hooks/formatters";
import { useMemo } from "react";

export type ProductionBasketRow = GridValidRowModel & {
  id: number | string;
  groupName: string;
  productGroupName?: string;
  target: number;
  steelGrades?: unknown;
};

type ProductionBasketsGridProps<TRow extends ProductionBasketRow> = Omit<
  DataGridPremiumProps<TRow>,
  "rows" | "columns" | "getRowId"
> & {
  rows: TRow[];
  renderGroupCell?: GridColDef<TRow>["renderCell"];
  renderSteelGradesCell?: GridColDef<TRow>["renderCell"];
};

const ProductionBasketsNoRowsOverlay = () => {
  const { t } = useTenantTranslation();
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
        px: 2,
        textAlign: "center",
        color: "text.secondary",
        fontSize: 14,
      }}
    >
      {t("productionBasketsNoRowsDescription")}
    </Box>
  );
};

export const ProductionBasketsGrid = <
  TRow extends ProductionBasketRow = ProductionBasketRow,
>({
  sx,
  slots,
  rows,
  renderGroupCell,
  renderSteelGradesCell,
  ...props
}: ProductionBasketsGridProps<TRow>) => {
  const { t } = useTenantTranslation();

  const columns = useMemo<GridColDef<TRow>[]>(() => {
    return [
      {
        field: "productGroupName",
        headerName: t("productGroup"),
        flex: 1,
        sortable: false,
      },
      {
        field: "groupName",
        headerName: t("chemistryGroup"),
        flex: 1,
        sortable: false,
        renderCell: renderGroupCell ?? ((params) => params.row.groupName),
      },
      {
        field: "steelGrades",
        headerName: t("steelGrades"),
        flex: 1,
        sortable: false,
        renderCell:
          renderSteelGradesCell ??
          ((params) => {
            const grades = Array.isArray(params.row.steelGrades)
              ? params.row.steelGrades.filter(Boolean)
              : [];
            if (grades.length === 0) {
              return "-";
            }
            const text = grades.join(", ");
            return (
              <Box
                component="span"
                sx={{
                  display: "block",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                title={text}
              >
                {text}
              </Box>
            );
          }),
      },
      {
        field: "target",
        headerName: t("targetBaskets"),
        width: 150,
        editable: true,
        sortable: false,
        align: "right",
        headerAlign: "right",
        renderCell: (params) => {
          // Handle group rows - show range of child values
          if (params.rowNode.type === "group") {
            const childIds = params.rowNode.children;
            const values = childIds
              .map((id: GridRowId) => {
                const row = params.api.getRow(id) as TRow | undefined;
                return row?.target;
              })
              .filter((v: number | undefined): v is number => v !== undefined);
            if (values.length === 0) return null;
            const min = Math.min(...values);
            const max = Math.max(...values);
            return min === max ? String(min) : `${min} – ${max}`;
          }
          return <NumberCell value={params.row.target} />;
        },
      },
    ];
  }, [renderGroupCell, renderSteelGradesCell, t]);

  const baseSx: SxProps<Theme> = {
    "& .MuiDataGrid-cell": {
      display: "flex",
      alignItems: "center",
    },
    "& .MuiDataGrid-cell--editing input": {
      textAlign: "right",
    },
  };

  const mergedSx: SxProps<Theme> = [baseSx].concat(
    sx ? (Array.isArray(sx) ? sx : [sx]) : []
  );

  return (
    <HotkeyCompliantDataGrid
      rows={rows}
      columns={columns}
      getRowId={(row) => row.id}
      hideFooter
      disableColumnMenu
      {...props}
      slots={{
        noRowsOverlay: ProductionBasketsNoRowsOverlay,
        ...slots,
      }}
      sx={mergedSx}
    />
  );
};
